/**
 * Where feature packages declare their routes.
 *
 * The composition root must not have to know that `feature-admin` exists in
 * order to mount its screens. It imports a feature's manifest, the manifest
 * contributes routes, and the tree assembles itself — the same reasoning as
 * the backend's `ModuleManifest`, and as `RouteRegistry` in the Flutter base
 * template: a feature joins the application by being declared, not by someone
 * editing a central list of screens.
 *
 * Routes carry their own access requirement rather than having it applied from
 * outside. A route and the permission it needs belong together; separating
 * them is how a new screen ends up unprotected because nobody remembered to
 * add it to the other list.
 */

import type { FunctionComponent } from "react";

import type { RouteRequirement } from "./gates";

/** One screen. */
export interface RouteDefinition {
  /** Absolute path, e.g. `/admin/users`. */
  path: string;
  component: FunctionComponent;
  /** What the principal must be or hold. Absent means fully public. */
  requirement?: RouteRequirement;
  /**
   * Which shell to render inside: the marketing/auth chrome or the signed-in
   * application chrome.
   */
  layout?: "public" | "app";
  /** Navigation entry, when this route should appear in a menu. */
  nav?: NavEntry;
}

/** A menu item derived from a route. */
export interface NavEntry {
  /** i18n key — never a literal string; the label is translated at render. */
  labelKey: string;
  /** Lower sorts first. */
  order?: number;
  /** Which menu it belongs to. */
  section?: "main" | "admin" | "account";
  /**
   * i18n key of the heading this entry sits under, inside its section.
   *
   * A flat list of a dozen destinations is a list nobody reads. Grouping is a
   * *presentation* concern, so it is a hint rather than a structure: entries
   * with no group render before the first heading, and a group whose every
   * entry the principal cannot reach disappears with them — which is why the
   * heading is derived from the entries rather than declared beside them.
   */
  group?: string;
  icon?: string;
}

/** What a feature package contributes to the application. */
export interface FeatureRoutes {
  /** Stable identifier, matching the backend module where there is one. */
  id: string;
  routes: readonly RouteDefinition[];
}

/**
 * Collect routes from features, rejecting duplicate paths.
 *
 * Two features claiming `/settings` is not a merge to resolve quietly —
 * whichever won would depend on import order, and the loser would simply be
 * missing with no error anywhere. It is a wiring mistake, and it fails at
 * startup where it is cheap to find.
 */
export function collectRoutes(features: readonly FeatureRoutes[]): readonly RouteDefinition[] {
  const byPath = new Map<string, string>();
  const routes: RouteDefinition[] = [];

  for (const feature of features) {
    for (const route of feature.routes) {
      const owner = byPath.get(route.path);
      if (owner !== undefined) {
        throw new Error(
          `Route "${route.path}" is declared by both "${owner}" and "${feature.id}". ` +
            "Paths must be unique across features.",
        );
      }
      byPath.set(route.path, feature.id);
      routes.push(route);
    }
  }

  return routes;
}

/**
 * The navigable entries of one menu section, in display order.
 *
 * Filtering by permission happens at render, not here: this returns everything
 * the section contains, and the navigation component hides what the current
 * principal cannot reach. Keeping the two apart means the menu and the router
 * agree by construction — both read the same `requirement`.
 */
export function navigationFor(
  routes: readonly RouteDefinition[],
  section: NavEntry["section"] = "main",
): readonly (RouteDefinition & { nav: NavEntry })[] {
  return routes
    .filter(
      (route): route is RouteDefinition & { nav: NavEntry } =>
        route.nav !== undefined && (route.nav.section ?? "main") === section,
    )
    .sort((a, b) => (a.nav.order ?? 0) - (b.nav.order ?? 0));
}

/** A route that has a menu entry. */
export type NavRoute = RouteDefinition & { nav: NavEntry };

/**
 * One thing an area collects: a single destination, every entry of a
 * feature's group, or the ungrouped entries of a section.
 */
export type NavAreaRule =
  | { readonly path: string }
  | { readonly group: string }
  | { readonly section: NonNullable<NavEntry["section"]> };

/**
 * A heading of the menu that gathers entries from several features.
 *
 * Features group their own entries (`NavEntry.group`), and a feature cannot
 * name a sibling's group. So "Sales" — customers from one feature, orders from
 * another, invoices from a third — can only be declared by the application,
 * which is allowed to know them all. The area says what it collects; the
 * entries, their labels and their permissions still come from the registry,
 * so an area lists destinations without being able to invent one.
 */
export interface NavArea {
  readonly id: string;
  /** In display order: entries are sorted by the first rule that took them. */
  readonly include: readonly NavAreaRule[];
}

/** One node of the arranged menu. */
export type NavNode<A extends NavArea, R extends NavRoute = NavRoute> =
  /** A declared area, with at least one entry. */
  | { readonly kind: "area"; readonly area: A; readonly entries: readonly R[] }
  /** A feature group no area took, kept under its own heading. */
  | { readonly kind: "group"; readonly group: string; readonly entries: readonly R[] }
  /** An ungrouped entry no area took. */
  | { readonly kind: "entry"; readonly entry: R };

/**
 * Arrange menu entries into the application's areas.
 *
 * A path rule beats a group rule, which beats a section rule — the more
 * specific claim wins, whichever area declares it, so an application can move
 * one settings screen out of its feature's group without taking the rest.
 *
 * Nothing is dropped. An entry no area takes keeps its feature group, after
 * the declared areas: a feature added later appears in the menu, unsorted,
 * rather than being reachable only by typing its URL. An area that took
 * nothing — every entry filtered out by permission — is absent, because a
 * heading over nothing is worse than no heading.
 */
export function arrangeNavigation<A extends NavArea, R extends NavRoute>(
  entries: readonly R[],
  areas: readonly A[],
): NavNode<A, R>[] {
  const claim = (route: R): { area: number; rule: number } | null => {
    const matchers: ((rule: NavAreaRule) => boolean)[] = [
      (rule) => "path" in rule && rule.path === route.path,
      (rule) => "group" in rule && rule.group === route.nav.group,
      (rule) =>
        "section" in rule &&
        route.nav.group === undefined &&
        rule.section === (route.nav.section ?? "main"),
    ];
    for (const matches of matchers) {
      for (const [area, { include }] of areas.entries()) {
        const rule = include.findIndex(matches);
        if (rule !== -1) return { area, rule };
      }
    }
    return null;
  };

  const claimed = areas.map(() => [] as { route: R; rule: number }[]);
  const unclaimed: NavNode<A, R>[] = [];
  const looseGroups = new Map<string, R[]>();

  for (const route of entries) {
    const found = claim(route);
    if (found) {
      claimed[found.area]?.push({ route, rule: found.rule });
    } else if (route.nav.group === undefined) {
      unclaimed.push({ kind: "entry", entry: route });
    } else {
      const group = looseGroups.get(route.nav.group);
      if (group) {
        group.push(route);
      } else {
        const fresh = [route];
        looseGroups.set(route.nav.group, fresh);
        unclaimed.push({ kind: "group", group: route.nav.group, entries: fresh });
      }
    }
  }

  const declared = areas.flatMap((area, index): NavNode<A, R>[] => {
    const taken = claimed[index] ?? [];
    if (taken.length === 0) return [];
    // `sort` is stable, so entries under one rule keep the order they arrived
    // in — the sections' own `order`.
    const ordered = [...taken].sort((a, b) => a.rule - b.rule).map(({ route }) => route);
    return [{ kind: "area", area, entries: ordered }];
  });

  return [...declared, ...unclaimed];
}

/**
 * The entry the reader is on, or inside of.
 *
 * Matched by path segment and by the longest path: `/clients/C001` is inside
 * `/clients`, `/admin/users` is `/admin/users` rather than `/admin`, and
 * `/clientsX` is inside nothing. A menu needs this to open the area holding a
 * detail page, which is never itself an entry.
 */
export function currentEntry<R extends { readonly path: string }>(
  entries: readonly R[],
  pathname: string,
): R | null {
  let best: R | null = null;
  for (const entry of entries) {
    const inside =
      pathname === entry.path ||
      pathname.startsWith(entry.path.endsWith("/") ? entry.path : `${entry.path}/`);
    if (inside && entry.path.length > (best?.path.length ?? -1)) best = entry;
  }
  return best;
}
